import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import GlobalKPICards from './components/GlobalKPICards';
import BehaviouralSafetySection from './components/BehaviouralSafetySection';
import ProcessSafetySection from './components/ProcessSafetySection';
import EnvironmentalMedicalSection from './components/EnvironmentalMedicalSection';
import ComplianceTrainingSection from './components/ComplianceTrainingSection';
import AIInsightsPanel from './components/AIInsightsPanel';
import ActionHub from './components/ActionHub';

const TIMELINE_OPTIONS = [
  { id: 'today', label: 'Today' },
  { id: 'current_month', label: 'Current Month' },
  { id: 'last_month', label: 'Last Month' },
  { id: 'ytd', label: 'YTD' },
  { id: 'custom', label: 'Custom Range' },
];

const formatDate = (date) => date?.toISOString()?.split('T')?.[0];

const SafetyControlTower = () => {
  const navigate = useNavigate();
  const [lastRefreshed] = useState(new Date()?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
  const [activeTimeline, setActiveTimeline] = useState('current_month');
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const today = new Date();
  const [customFrom, setCustomFrom] = useState(formatDate(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [customTo, setCustomTo] = useState(formatDate(today));
  const [appliedCustom, setAppliedCustom] = useState(null);
  const pickerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (pickerRef?.current && !pickerRef?.current?.contains(e?.target)) {
        setShowCustomPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleTimelineSelect = (id) => {
    setActiveTimeline(id);
    if (id === 'custom') {
      setShowCustomPicker(true);
    } else {
      setShowCustomPicker(false);
      setAppliedCustom(null);
    }
  };

  const handleApplyCustom = () => {
    setAppliedCustom({ from: customFrom, to: customTo });
    setShowCustomPicker(false);
  };

  const getTimelineLabel = () => {
    if (activeTimeline === 'custom' && appliedCustom) {
      return `${appliedCustom?.from} → ${appliedCustom?.to}`;
    }
    return TIMELINE_OPTIONS?.find((o) => o?.id === activeTimeline)?.label || '';
  };

  const timelineContext = { period: activeTimeline, customRange: appliedCustom };

  return (
    <div className="min-h-screen bg-background p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name="ChevronLeft" size={14} />
              Dashboard
            </button>
          </div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
              <Icon name="Tower" size={20} color="var(--color-primary)" />
            </div>
            Safety Control Tower
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Leadership command centre — all critical safety signals in one view
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-2 bg-card border border-border rounded-lg text-xs text-muted-foreground">
            <Icon name="RefreshCw" size={13} />
            <span>Refreshed {lastRefreshed}</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-xs font-medium text-emerald-400">Live</span>
          </div>
          <button className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors">
            <Icon name="Download" size={15} />
            Export Report
          </button>
        </div>
      </div>
      {/* Timeline Filter Bar */}
      <div className="bg-card border border-border rounded-xl px-4 py-3">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider shrink-0">
            <Icon name="CalendarDays" size={14} />
            Timeline
          </div>
          <div className="flex flex-wrap items-center gap-2 flex-1">
            {TIMELINE_OPTIONS?.map((opt) => (
              <button
                key={opt?.id}
                onClick={() => handleTimelineSelect(opt?.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                  activeTimeline === opt?.id
                    ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                    : 'bg-background text-muted-foreground border-border hover:border-primary/50 hover:text-foreground'
                }`}
              >
                {opt?.label}
              </button>
            ))}
          </div>
          {/* Active period badge */}
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 border border-primary/20 rounded-lg shrink-0">
            <Icon name="Clock" size={12} color="var(--color-primary)" />
            <span className="text-xs font-medium text-primary">{getTimelineLabel()}</span>
          </div>
        </div>

        {/* Custom Date Range Picker */}
        {showCustomPicker && (
          <div ref={pickerRef} className="mt-3 pt-3 border-t border-border">
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex flex-col gap-1">
                <label className="text-xs text-muted-foreground font-medium">From</label>
                <input
                  type="date"
                  value={customFrom}
                  max={customTo}
                  onChange={(e) => setCustomFrom(e?.target?.value)}
                  className="px-3 py-1.5 bg-background border border-border rounded-lg text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs text-muted-foreground font-medium">To</label>
                <input
                  type="date"
                  value={customTo}
                  min={customFrom}
                  max={formatDate(today)}
                  onChange={(e) => setCustomTo(e?.target?.value)}
                  className="px-3 py-1.5 bg-background border border-border rounded-lg text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                />
              </div>
              <button
                onClick={handleApplyCustom}
                className="px-4 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs font-medium hover:bg-primary/90 transition-colors"
              >
                Apply Range
              </button>
              <button
                onClick={() => { setShowCustomPicker(false); setActiveTimeline('current_month'); }}
                className="px-3 py-1.5 bg-background border border-border text-muted-foreground rounded-lg text-xs font-medium hover:text-foreground transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
      {/* Global KPI Band */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Global KPIs</span>
          <div className="flex-1 h-px bg-border" />
        </div>
        <GlobalKPICards timeline={timelineContext} />
      </div>
      {/* Behavioural Safety */}
      <BehaviouralSafetySection timeline={timelineContext} />
      {/* Process Safety */}
      <ProcessSafetySection timeline={timelineContext} />
      {/* Environmental & Medical */}
      <EnvironmentalMedicalSection timeline={timelineContext} />
      {/* Compliance & Training */}
      <ComplianceTrainingSection timeline={timelineContext} />
      {/* AI Insights */}
      <AIInsightsPanel timeline={timelineContext} />
      {/* Action Hub */}
      <ActionHub timeline={timelineContext} />
    </div>
  );
};

export default SafetyControlTower;